import React, { useState, useEffect } from 'react';
import {
  useRecordContext,
  useDataProvider,
  useNotify,
  Datagrid,
  TextField,
  DateField,
  ChipField,
  FunctionField,
  ReferenceManyField,

} from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  IconButton,
  Tooltip,
  Divider,
  Grid,
  Chip,
  Stack,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField as MuiTextField,
  MenuItem,
} from '@mui/material';
import {
  Link as LinkIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  Task as TaskIcon,
  Support as SupportIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { TaskDialog } from '../../components/TaskDialog';
import { useGetList, useListContext } from 'react-admin';

// Custom component to handle tasks list with proper empty state
const TasksList: React.FC<{ onRowClick: (id: string, data: any) => boolean }> = ({
  onRowClick,
}) => {
  const { data, isLoading, total } = useListContext();

  if (isLoading) {
    return <Typography color="text.secondary">Loading tickets...</Typography>;
  }

  if (!data || data.length === 0 || total === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
        <TaskIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          No service requests found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Click "Add Service Request" to create a new request for this customer
        </Typography>
      </Paper>
    );
  }

  return (
    <Datagrid bulkActionButtons={false} rowClick={onRowClick}>
      <TextField source="title" label="Title" />
      <ChipField source="priority" label="Priority" />
      <DateField source="createdAt" label="Created" />
      <DateField source="scheduledDate" label="Scheduled Date" />
    </Datagrid>
  );
};

export const CustomerPortalTab: React.FC = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [portalLink, setPortalLink] = useState<string>('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [loadingLink, setLoadingLink] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [createTaskDialogOpen, setCreateTaskDialogOpen] = useState(false);
  const [loadingTask, setLoadingTask] = useState(false);

  const handleTaskClick = async (id: string, data: any) => {
    try {
      setLoadingTask(true);
      // Fetch complete task details including relations
      const response = await dataProvider.getOne('tasks', { id });
      setSelectedTask(response.data);
      setTaskDialogOpen(true);
    } catch (error) {
      console.error('Failed to load task details:', error);
      notify('Failed to load task details', { type: 'error' });
    } finally {
      setLoadingTask(false);
    }
    return false; // Prevent default navigation
  };

  useEffect(() => {
    if (record?.id) {
      generatePortalLink();
    }
  }, [record?.id]);

  const generatePortalLink = async () => {
    if (!record?.id) return;

    try {
      setLoadingLink(true);
      const apiUrl = import.meta.env.VITE_SIMPLE_REST_URL || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/crm/customers/portal-link/${record.id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          'x-tenant-id': localStorage.getItem('selectedTenant') || 'default',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to generate portal link');
      }

      const data = await response.json();
      const baseUrl = window.location.origin;
      const fullLink = `${baseUrl}/portal/${data.token}`;
      setPortalLink(fullLink);
    } catch (error) {
      console.error('Error generating portal link:', error);
      notify('Failed to generate portal link', { type: 'error' });
    } finally {
      setLoadingLink(false);
    }
  };

  const handleCopyLink = () => {
    if (portalLink) {
      navigator.clipboard.writeText(portalLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
      notify('Portal link copied to clipboard', { type: 'success' });
    }
  };

  const handleRegenerateLink = async () => {
    await generatePortalLink();
    notify('Portal link regenerated', { type: 'success' });
  };

  if (!record) return null;

  return (
    <Grid container spacing={3}>
      {/* Portal Access Card */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
            >
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinkIcon color="primary" />
                Customer Portal Access
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleRegenerateLink}
                disabled={loadingLink}
              >
                Regenerate Link
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Share this secure link with your customer to give them access to their portal:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                    p: 1.5,
                    bgcolor: 'white',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.300',
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {loadingLink ? 'Generating link...' : portalLink}
                </Typography>
                <Tooltip title={linkCopied ? 'Copied!' : 'Copy link'}>
                  <IconButton
                    onClick={handleCopyLink}
                    disabled={loadingLink || !portalLink}
                    sx={{
                      bgcolor: linkCopied ? 'success.main' : 'primary.main',
                      color: 'white',
                      '&:hover': {
                        bgcolor: linkCopied ? 'success.dark' : 'primary.dark',
                      },
                      '&:disabled': {
                        bgcolor: 'grey.300',
                        color: 'grey.500',
                      },
                    }}
                  >
                    {linkCopied ? <CheckIcon /> : <ContentCopyIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                This link provides secure access to view service requests, invoices, and company
                information.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Customer Service Tickets */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
            >
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SupportIcon color="primary" />
                Service Requests
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setCreateTaskDialogOpen(true)}
              >
                Add Service Request
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <ReferenceManyField
              reference="tasks"
              target="customerId"
              sort={{ field: 'createdAt', order: 'DESC' }}
              perPage={50}
              emptyText=" "
            >
              <TasksList onRowClick={handleTaskClick} />
            </ReferenceManyField>
          </CardContent>
        </Card>
      </Grid>

      {/* Task Dialog (Create & Edit) */}
      <TaskDialog
        open={taskDialogOpen || createTaskDialogOpen}
        onClose={() => {
          setTaskDialogOpen(false);
          setCreateTaskDialogOpen(false);
          setSelectedTask(null);
        }}
        task={taskDialogOpen ? selectedTask : undefined}
        customerId={record?.id}
        onSuccess={() => {
          window.location.reload();
        }}
        onDelete={() => {
          window.location.reload();
        }}
      />
    </Grid>
  );
};
